import * as SelectPrimitive from "@radix-ui/react-select";
import { cva } from "class-variance-authority";
import type { ComponentPropsWithoutRef } from "react";
import type { FieldError } from "react-hook-form";
import { cn } from "../lib/utils";
import { Icon, type IconSymbol } from "./icon";
import { InlineFeedback, InlineFeedbackGroup } from "./inline-feedback";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;
// Define cva for class variance management
const selectTriggerClasses = cva(
  "group flex items-center justify-start rounded border bg-white px-2 min-h-6 gap-2 text-caption font-title focus:outline-none disabled:cursor-not-allowed [&>span]:line-clamp-1 disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-600 text-start w-full",
  {
    variants: {
      floatingPlaceholder: {
        true: "pl-4 h-8",
        false: "pl-2",
      },
      error: {
        true: "border-red-500",
        false: "border-gray-200",
      },
      variant: {
        primary: "hover:border-primary data-[state=open]:border-primary",
        secondary: "hover:border-gray-500 data-[state=open]:border-gray-500",
      },
    },
    defaultVariants: {
      floatingPlaceholder: false,
      error: false,
      variant: "primary",
    },
  },
);

const iconClasses = cva("transition-colors group-disabled:text-gray-500", {
  variants: {
    variant: {
      primary: "text-primary",
      secondary: "text-gray-500",
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

interface SelectTriggerProps
  extends ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  leftIcon?: IconSymbol;
  floatingPlaceholder?: boolean;
  placeholder?: string;
  helperText?: string;
  error?: FieldError;
  variant?: "primary" | "secondary";
  ref?: React.Ref<HTMLButtonElement>;
}

interface SelectTriggerComponentProps extends SelectTriggerProps {
  ref?: React.Ref<HTMLButtonElement>;
}
const SelectTrigger: React.FC<SelectTriggerComponentProps> = ({
  className,
  children,
  leftIcon,
  floatingPlaceholder,
  placeholder,
  helperText,
  error,
  variant = "primary",
  ref,
  ...props
}) => (
  <div className="w-full">
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        selectTriggerClasses({ floatingPlaceholder, error: !!error, variant }),
        className,
      )}
      {...props}
    >
      {!floatingPlaceholder && leftIcon && (
        <div className="flex items-center">
          <Icon
            icon={leftIcon}
            size={16}
            className={cn("text-gray-500", "group-disabled:opacity-50")}
          />
        </div>
      )}
      {floatingPlaceholder ? (
        <div className="flex flex-col w-full !leading-[19px]">
          <span className="text-caption font-base text-muted-foreground mb-[-4px]">
            {placeholder}
          </span>
          <span className="line-clamp-1">{children || placeholder}</span>
        </div>
      ) : (
        children
      )}
      <SelectPrimitive.Icon asChild className="ml-auto">
        <Icon
          icon={"chevron-up"}
          className={iconClasses({ variant })}
          size={16}
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>

    {(error?.message || helperText) && (
      <InlineFeedbackGroup>
        {error?.message && (
          <InlineFeedback variant="critical" message={error.message} nested />
        )}
        {helperText && (
          <InlineFeedback variant="info" message={helperText} nested />
        )}
      </InlineFeedbackGroup>
    )}
  </div>
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

interface SelectScrollUpButtonProps
  extends ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton> {
  variant?: "primary" | "secondary";
  ref?: React.Ref<HTMLDivElement>;
}
const SelectScrollUpButton: React.FC<SelectScrollUpButtonProps> = ({
  className,
  variant,
  ref,
  ...props
}) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <Icon icon={"chevron-up"} className={iconClasses({ variant })} size={16} />
  </SelectPrimitive.ScrollUpButton>
);
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

interface SelectScrollDownButtonProps
  extends ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton> {
  variant?: "primary" | "secondary";
  ref?: React.Ref<HTMLDivElement>;
}
const SelectScrollDownButton: React.FC<SelectScrollDownButtonProps> = ({
  className,
  variant,
  ref,
  ...props
}) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <Icon
      icon={"chevron-down"}
      className={iconClasses({ variant })}
      size={16}
    />
  </SelectPrimitive.ScrollDownButton>
);
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

interface SelectContentComponentProps
  extends ComponentPropsWithoutRef<typeof SelectPrimitive.Content> {
  variant?: "primary" | "secondary";
  ref?: React.Ref<HTMLDivElement>;
  children?: React.ReactNode;
}
const SelectContent: React.FC<SelectContentComponentProps> = ({
  className,
  children,
  position = "popper",
  variant,
  ref,
  ...props
}) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-96 text-caption font-title overflow-hidden rounded border border-gray-200 bg-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton variant={variant} />
      <SelectPrimitive.Viewport
        className={cn(
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton variant={variant} />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

interface SelectLabelComponentProps
  extends ComponentPropsWithoutRef<typeof SelectPrimitive.Label> {
  ref?: React.Ref<HTMLDivElement>;
}
const SelectLabel: React.FC<SelectLabelComponentProps> = ({
  className,
  ref,
  ...props
}) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "h-6 text-caption font-semibold px-2 bg-gray-50 flex items-center",
      className,
    )}
    {...props}
  />
);
SelectLabel.displayName = SelectPrimitive.Label.displayName;

interface SelectItemComponentProps
  extends ComponentPropsWithoutRef<typeof SelectPrimitive.Item> {
  ref?: React.Ref<HTMLDivElement>;
  children?: React.ReactNode;
}
const SelectItem: React.FC<SelectItemComponentProps> = ({
  className,
  children,
  ref,
  ...props
}) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer px-2 h-6 items-center hover:bg-gray-50 outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
);
SelectItem.displayName = SelectPrimitive.Item.displayName;

interface SelectSeparatorComponentProps
  extends ComponentPropsWithoutRef<typeof SelectPrimitive.Separator> {
  ref?: React.Ref<HTMLDivElement>;
}
const SelectSeparator: React.FC<SelectSeparatorComponentProps> = ({
  className,
  ref,
  ...props
}) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
);
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
